import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Trash2, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../../ui/Card';
import Button from '../../../ui/Button';
import { LoadingSpinner } from '../../../ui/Loading';
import { useVfxSchedules } from '../../../../hooks/useApi';
import { VfxSchedule } from '../../../../types';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../ui/Table';

interface ScheduleListProps {
  onDelete: (schedule: VfxSchedule) => void;
}

const ScheduleList: React.FC<ScheduleListProps> = ({ onDelete }) => {
  const navigate = useNavigate();
  const fmt = useFormatDate();
  const { data: schedulesData, isLoading, error, refetch } = useVfxSchedules();

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Active
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Inactive
      </span>
    );
  };

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-destructive">Failed to load schedules</p>
            <Button onClick={() => refetch()} className="mt-4" variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  const schedules = schedulesData?.data || [];

  if (schedules.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center h-64">
          <p className="text-muted-foreground mb-4">No schedules found</p>
          <p className="text-sm text-muted-foreground">
            Create your first schedule to get started
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Start Time</TableHead>
              <TableHead>End Time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedules.map((schedule) => (
              <TableRow
                key={schedule.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/vfx-config/schedules/${schedule.id}`)}
              >
                <TableCell className="font-medium">{schedule.name}</TableCell>
                <TableCell>
                  {fmt(schedule.startTime)}
                </TableCell>
                <TableCell>
                  {fmt(schedule.endTime)}
                </TableCell>
                <TableCell>{getStatusBadge(schedule.isActive)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/vfx-config/schedules/${schedule.id}`)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(schedule)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default ScheduleList;

